/**
 * MYA Living Energy Field — spatial configuration (v2).
 *
 * MYA never uses "the whole page" as her coordinate system. Placement is
 * described by a TERRITORY that is positioned inside the mounted surface:
 *
 *  - Home Zone      MYA's normal anchor territory and centre of mass. At idle
 *                   her body stays concentrated inside this radius.
 *  - Expansion Zone The maximum territory her field may EVER reach. It is a
 *                   boundary, not a size: the idle entity never fills it, but
 *                   the simulation clamps against it so future filaments and
 *                   tendrils can extend outward without inflating the entity.
 *
 * A `MyaTerritory` describes where that pair of zones sits inside the host
 * surface (fractions of the surface, so it stays responsive and never depends
 * on fixed screen pixels). Each page can supply its own territory; the field
 * renderer itself only ever sees the square field box the territory resolves
 * to, so the renderer has no knowledge of the page layout at all.
 */

import type { MyaPalette } from "./palette";
import type { MyaTuning } from "./tuning";

export type MyaZoneConfig = {
  /** Anchor point inside the field box, 0..1 of width/height. */
  anchor: { x: number; y: number };
  /** Normal idle radius, as a fraction of min(field width, field height). */
  homeRadius: number;
  /** Hard outer boundary for any outward energy, same units. */
  expansionRadius: number;
};

export type MyaVisualQuality = "low" | "standard";

export type MyaVisualConfig = {
  zone: MyaZoneConfig;
  quality: MyaVisualQuality;
  /** Global intensity multiplier for luminosity (0..1). */
  intensity: number;
  /**
   * Optional procedural parameter set. Omitted in production, where the engine
   * falls back to `DEFAULT_MYA_TUNING`. The development sandbox supplies it.
   */
  tuning?: MyaTuning;
  /**
   * Optional colour palette (paint-time only). Omitted in production, where
   * the engine renders the Original gold palette exactly as before.
   */
  palette?: MyaPalette;
};

/**
 * Placement of MYA's zones inside a host surface.
 *
 * `anchor` is the territory centre as a fraction of the surface. The territory
 * is RECTANGULAR: widths are fractions of surface width, heights are fractions
 * of surface height, so it stays responsive and never depends on fixed screen
 * pixels. Each page can supply its own territory.
 */
export type MyaTerritory = {
  anchor: { x: number; y: number };
  homeWidth: number;
  homeHeight: number;
  expansionWidth: number;
  expansionHeight: number;
};

/**
 * Admin Dashboard territory.
 *
 * Deliberately NOT the centre of the dashboard: MYA occupies her own
 * designated rectangular region in the open middle-right area of the dashboard
 * surface — below the Commercial Funnel / Product Performance block and above
 * the Annual Voting / Support Operations area — so the dashboard content layer
 * underneath stays the visual subject of the page and the funnel at the top is
 * never overlapped.
 */
export const ADMIN_DASHBOARD_MYA_TERRITORY: MyaTerritory = {
  anchor: { x: 0.665, y: 0.33 },
  homeWidth: 0.2,
  homeHeight: 0.23,
  expansionWidth: 0.26,
  expansionHeight: 0.38,
};


/** CSS placement of the rectangular Expansion-Zone field box for a territory. */
export function territoryFieldStyle(
  territory: MyaTerritory,
): {
  left: string;
  top: string;
  width: string;
  height: string;
  transform: string;
} {
  return {
    left: `${territory.anchor.x * 100}%`,
    top: `${territory.anchor.y * 100}%`,
    width: `${territory.expansionWidth * 100}%`,
    height: `${territory.expansionHeight * 100}%`,
    transform: "translate(-50%, -50%)",
  };
}

/**
 * Zone config for the field box a territory resolves to. Inside that box the
 * anchor is the centre, the expansion boundary is the box edge, and the Home
 * Zone keeps its real proportion of the territory. Radii are relative to the
 * box's SHORTER axis, so a rectangular territory still fully contains both
 * zones on every axis.
 */
export function territoryZone(territory: MyaTerritory): MyaZoneConfig {
  // The field box's radii are resolved against its SHORTER pixel axis, which
  // for these wide-surface territories is the box width. Sizing the Home Zone
  // from the width ratio therefore makes the Home Zone circle's diameter equal
  // the territory's real home width — the reference circle the idle body is
  // meant to fill — instead of shrinking it to the height ratio.
  const homeRatio = territory.homeWidth / territory.expansionWidth;
  return {
    anchor: { x: 0.5, y: 0.5 },
    homeRadius: 0.5 * homeRatio,
    expansionRadius: 0.5,
  };
}

/** How much larger the Expansion Zone is than the Home Zone. */
export const MYA_EXPANSION_FIELD_SCALE =
  ADMIN_DASHBOARD_MYA_TERRITORY.expansionWidth /
  ADMIN_DASHBOARD_MYA_TERRITORY.homeWidth;

export const DASHBOARD_MYA_ZONE: MyaZoneConfig = territoryZone(
  ADMIN_DASHBOARD_MYA_TERRITORY,
);


export const DEFAULT_MYA_VISUAL_CONFIG: MyaVisualConfig = {
  zone: DASHBOARD_MYA_ZONE,
  quality: "standard",
  intensity: 1,
};

/** Resolve fractional zone config into pixel space for a given container. */
export function resolveZone(
  zone: MyaZoneConfig,
  width: number,
  height: number,
): { cx: number; cy: number; home: number; expansion: number } {
  const base = Math.max(1, Math.min(width, height));
  const home = Math.max(1, zone.homeRadius * base);
  const expansion = Math.max(home, zone.expansionRadius * base);
  return {
    cx: zone.anchor.x * width,
    cy: zone.anchor.y * height,
    home,
    expansion,
  };
}

/** Zone configs must describe a real boundary larger than the home territory. */
export function isValidZone(zone: MyaZoneConfig): boolean {
  return (
    zone.homeRadius > 0 &&
    zone.expansionRadius >= zone.homeRadius &&
    zone.anchor.x >= 0 &&
    zone.anchor.x <= 1 &&
    zone.anchor.y >= 0 &&
    zone.anchor.y <= 1
  );
}

/** Territories must sit inside the surface and describe a real boundary. */
export function isValidTerritory(t: MyaTerritory): boolean {
  const withinSurface =
    t.anchor.x - t.expansionWidth / 2 >= -1e-9 &&
    t.anchor.x + t.expansionWidth / 2 <= 1 + 1e-9 &&
    t.anchor.y - t.expansionHeight / 2 >= -1e-9 &&
    t.anchor.y + t.expansionHeight / 2 <= 1 + 1e-9;
  return (
    t.homeWidth > 0 &&
    t.homeHeight > 0 &&
    t.expansionWidth > t.homeWidth &&
    t.expansionHeight > t.homeHeight &&
    withinSurface
  );
}

